import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { MainService } from './main.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class BadgeService {
  public badgeCount = 0;
  public mediaCount = 0;
  public smsCount = 0;
  public badgeInfo: any;

  /** Chỉ hỏi quyền hiện badge một lần mỗi phiên, tránh làm phiền người dùng */
  private permissionAsked = false;

  constructor(
    private mainService: MainService,
    private localStorage: StorageService,
  ) { }

  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * iOS bắt buộc phải có quyền mới hiện được badge trên icon.
   * Android và Web không cần, luôn trả về granted.
   */
  private async ensurePermission(): Promise<boolean> {
    if (!this.isNative) {
      return false;
    }
    try {
      const status = await Badge.checkPermissions();
      if (status.display === 'granted') {
        return true;
      }
      if (status.display === 'denied' || this.permissionAsked) {
        return false;
      }
      this.permissionAsked = true;
      const asked = await Badge.requestPermissions();
      return asked.display === 'granted';
    } catch (e) {
      return false;
    }
  }

  /**
   * Hỏi server số tin chưa đọc rồi đặt lại badge cho khớp.
   * Đây là nguồn sự thật duy nhất — badge do APNs/FCM đặt sẽ bị ghi đè.
   */
  public getUnReadCount(userId): Promise<number> {
    return new Promise((resolve) => {
      if (!userId) {
        this.clear();
        resolve(0);
        return;
      }
      this.badgeCount = 0;
      this.mainService.getUnReadMessageCount(userId).subscribe(
        (res) => {
          this.mediaCount = res?.mediaCount || 0;
          this.smsCount = res?.smsCount || 0;
          this.badgeCount = this.mediaCount + this.smsCount;
          this.badgeInfo = res;
          this.applyToIcon(this.badgeCount);
          resolve(this.badgeCount);
        },
        () => {
          // Lỗi mạng thì giữ nguyên badge hiện tại, đừng xoá oan
          resolve(this.badgeCount);
        }
      );
    });
  }

  /**
   * Đồng bộ lại badge với tài khoản đang đăng nhập.
   * Gọi khi app quay lại foreground.
   */
  public async refresh(): Promise<void> {
    try {
      const student = await this.localStorage.getSelectedStudent();
      if (student && student.id) {
        await this.getUnReadCount(student.id);
      } else {
        await this.clear();
      }
    } catch (e) {
      // không làm gì
    }
  }

  /** Xoá sạch badge và các thông báo còn đọng. Gọi khi logout. */
  public async clear(): Promise<void> {
    this.badgeCount = 0;
    this.mediaCount = 0;
    this.smsCount = 0;
    if (!this.isNative) {
      return;
    }
    try {
      await Badge.clear();
    } catch (e) {
      // không làm gì
    }
    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (e) {
      // không làm gì
    }
  }

  private async applyToIcon(count: number): Promise<void> {
    if (!this.isNative) {
      return;
    }
    if (!count || count <= 0) {
      await this.clear();
      return;
    }
    const allowed = await this.ensurePermission();
    if (!allowed) {
      return;
    }
    try {
      await Badge.set({ count });
    } catch (e) {
      // không làm gì
    }
  }
}
